import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/src/contexts/auth.context";
import gqlClient from "../graphql.client";
import { syncStarTransactionAmountPolicy } from "./star-transaction-policy.api";
import { CREATE_SEASON_MUTATION, CREATE_STAR_TRANSACTION_MUTATION, CREATE_USER_STAR_BALANCE_MUTATION, DELETE_STAR_TRANSACTION_MUTATION, GET_ALL_SEASONS_QUERY, GET_CURRENT_ACTIVE_SEASON_QUERY, GET_SEASON_STAR_BALANCES_QUERY, GET_SEASON_STAR_TRANSACTIONS_QUERY, GET_STAR_TRANSACTIONS_QUERY, GET_USER_STAR_BALANCE_QUERY, UPDATE_SEASON_CONFIG_MUTATION, UPDATE_SEASON_MUTATION, UPDATE_USER_STAR_BALANCE_MUTATION } from "./rewardly.query";

export const useCreateSeason = (orgId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: {
      name: string;
      startDate?: Date | string;
      endDate?: Date | string;
      maxGiveAmt?: number;
      minGiveAmt?: number;
      status?: string;
      seasonAllowance: number
    }) => {
      const client = await gqlClient();
      return client.request(CREATE_SEASON_MUTATION, variables);
    },
    onSuccess: (data, variables) => {
      if (orgId) {
        queryClient.invalidateQueries({
          queryKey: ["currentActiveSeason", orgId]
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["currentActiveSeason"]
        });
      }
    },
  });
};

export const useSyncStarTransactionPolicy = () =>
  useMutation({
    mutationFn: ({
      minGiveAmt,
      maxGiveAmt,
    }: {
      minGiveAmt: number;
      maxGiveAmt: number;
    }) => syncStarTransactionAmountPolicy(minGiveAmt, maxGiveAmt),
  });

export const useGetCurrentActiveSeason = (orgId: string) => {
  return useQuery({
    queryKey: ["currentActiveSeason", orgId],
    queryFn: async () => {
      const client = await gqlClient();
      return client.request(GET_CURRENT_ACTIVE_SEASON_QUERY, { orgId });
    },
    enabled: !!orgId,
  });
};

export const useGetAllSeasons = (orgId: string) => {
  return useQuery({
    queryKey: ["allSeasons", orgId],
    queryFn: async () => {
      const client = await gqlClient();
      return client.request(GET_ALL_SEASONS_QUERY, { orgId });
    },
    enabled: !!orgId,
  });
};

export const useUpdateSeason = () => {
  return useMutation({
    mutationFn: async (variables: {
      filter: string;
      status?: string;
      endDate?: Date;
    }) => {
      const client = await gqlClient();
      return client.request(UPDATE_SEASON_MUTATION, variables);
    },
  });
};

export const useUpdateSeasonConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: {
      filter: string;
      maxGiveAmt?: number;
      minGiveAmt?: number;
      seasonAllowance?: number;
      endDate?: Date | string;
    }) => {
      const client = await gqlClient();
      return client.request(UPDATE_SEASON_CONFIG_MUTATION, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currentActiveSeason"] });
      queryClient.invalidateQueries({ queryKey: ["allSeasons"] });
    },
  });
};

export const useCreateUserStarBalance = () => {
  return useMutation({
    mutationFn: async (variables: {
      userId: string;
      seasonId: string;
      seasonAllowance: number;
      userInfo: {
        email: string;
        profileImageUrl?: string;
        userId: string;
        displayName: string;
      };
    }) => {
      const client = await gqlClient();
      return client.request(CREATE_USER_STAR_BALANCE_MUTATION, variables);
    },
  });
};

export const useGetUserStarBalance = (userId: string, seasonId: string) => {
  const { orgId } = useAuth();
  return useQuery({
    queryKey: ["userStarBalance", orgId, userId, seasonId],
    queryFn: async () => {
      const client = await gqlClient();
      return client.request(GET_USER_STAR_BALANCE_QUERY, { userId, seasonId, orgId });
    },
    enabled: !!orgId && !!userId && !!seasonId,
  });
};


export const useGetSeasonStarBalancesInfinite = (seasonId: string, pageSize = 20) => {
  const { orgId } = useAuth();
  return useInfiniteQuery({
    queryKey: ["seasonStarBalances", orgId, seasonId],
    queryFn: async ({ pageParam = 1 }) => {
      const client = await gqlClient();
      return client.request(GET_SEASON_STAR_BALANCES_QUERY, {
        seasonId,
        orgId,
        pageNo: pageParam,
        pageSize,
      });
    },
    getNextPageParam: (lastPage) => {
      if (lastPage?.getuserStarBalances?.hasNextPage) {
        return lastPage.getuserStarBalances.pageNo + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: !!orgId && !!seasonId,
  });
};

export const useUpdateUserStarBalance = () => {
  const queryClient = useQueryClient();
  const { orgId } = useAuth();

  return useMutation({
    mutationFn: async (variables: {
      userId: string;
      seasonId: string;
      starsRemaining: number;
    }) => {
      const client = await gqlClient();
      return client.request(UPDATE_USER_STAR_BALANCE_MUTATION, { ...variables, orgId });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["userStarBalance", variables.userId, variables.seasonId]
      });
      queryClient.invalidateQueries({
        queryKey: ["userStarBalances"]
      });
    },
  });
};

export const useCreateStarTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: {
      seasonId: string;
      senderId: string;
      recipientId: string;
      senderInfo: {
        email: string;
        profileImageUrl?: string;
        userId: string;
        displayName: string;
      };
      recipientInfo: {
        email: string;
        profileImageUrl?: string;
        userId: string;
        displayName: string;
      };
      amount: number;
      message?: string;
      type: string;
    }) => {
      const client = await gqlClient();
      return client.request(CREATE_STAR_TRANSACTION_MUTATION, variables);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["userStarBalance", variables.senderId, variables.seasonId]
      });
      queryClient.invalidateQueries({
        queryKey: ["starTransactions"]
      });
    },
  });
};

export const useDeleteStarTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transactionId: string) => {
      const client = await gqlClient();
      return client.request(DELETE_STAR_TRANSACTION_MUTATION, {
        filter: JSON.stringify({ ItemId: transactionId }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["starTransactions"] });
      queryClient.invalidateQueries({ queryKey: ["seasonTransactions"] });
    },
  });
};

export const useGetStarTransactions = (seasonId: string, page = 1, pageSize = 20) => {
  const { orgId } = useAuth();
  return useQuery({
    queryKey: ["starTransactions", orgId, seasonId, page, pageSize],
    queryFn: async () => {
      const client = await gqlClient();
      return client.request(GET_STAR_TRANSACTIONS_QUERY, {
        pageNo: page,
        pageSize,
        seasonId,
        orgId,
      });
    },
    enabled: !!orgId && !!seasonId,
  });
};


export const useGetSeasonStarTransactions = (seasonId: string) => {
  const { orgId } = useAuth();
  return useQuery({
    queryKey: ["seasonTransactions", orgId, seasonId],
    queryFn: async () => {
      const client = await gqlClient();
      return client.request(GET_SEASON_STAR_TRANSACTIONS_QUERY, {
        seasonId,
        orgId,
      });
    },
    enabled: !!orgId && !!seasonId,
  });
};

export const useGetStarTransactionsInfinite = (seasonId: string, pageSize = 20) => {
  const { orgId } = useAuth();
  return useInfiniteQuery({
    queryKey: ["starTransactions", orgId, seasonId],
    queryFn: async ({ pageParam = 1 }) => {
      const client = await gqlClient();
      return client.request(GET_STAR_TRANSACTIONS_QUERY, {
        pageNo: pageParam,
        pageSize,
        seasonId,
        orgId
      });
    },
    getNextPageParam: (lastPage) => {
      if (lastPage?.getstarTransactions?.hasNextPage) {
        return lastPage.getstarTransactions.pageNo + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: !!orgId && !!seasonId,
  });
};
